// src/pages/ConfidencePulseResponse.jsx
// Public page for clients to respond to confidence pulse surveys

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Clock, Heart, Shield, Phone, Loader2 } from 'lucide-react';

const BASE = process.env.REACT_APP_API_URL || '';

export default function ConfidencePulseResponse() {
  const { pulseId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pulseData, setPulseData] = useState(null);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Form state
  const [q1, setQ1] = useState('');
  const [q2, setQ2] = useState('');
  const [q3, setQ3] = useState('');
  const [customAnswer, setCustomAnswer] = useState('');
  const [openResponse, setOpenResponse] = useState('');

  useEffect(() => {
    async function loadPulse() {
      try {
        const res = await fetch(`${BASE}/confidence-pulse/${pulseId}`);
        const data = await res.json();

        if (!res.ok) {
          setError(data.error || 'Failed to load survey');
          return;
        }

        if (data.data.status === 'completed') {
          setSubmitted(true);
        } else if (data.data.status === 'expired') {
          setError('This survey has expired.');
        }

        setPulseData(data.data);
      } catch (err) {
        setError('Failed to load survey. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    loadPulse();
  }, [pulseId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!q1 || !q2 || !q3) {
      setError('Please answer all three questions');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const res = await fetch(`${BASE}/confidence-pulse/${pulseId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          q1,
          q2,
          q3,
          customAnswer: customAnswer || null,
          openResponse: openResponse || null
        })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Failed to submit response');
        return;
      }

      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#172A3A] to-[#20344A] flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#FFB800] animate-spin mx-auto" />
          <p className="mt-4 text-white/80">Loading...</p>
        </div>
      </div>
    );
  }

  if (submitted || pulseData?.status === 'completed') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#172A3A] to-[#20344A] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h1>
          <p className="text-slate-600 mb-6">
            Your feedback has been received. Your agent {pulseData?.agentFirstName ? `(${pulseData.agentFirstName})` : ''} will follow up if needed.
          </p>
          <p className="text-sm text-slate-500">You can close this page now.</p>
        </div>
      </div>
    );
  }

  if (error && !pulseData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#172A3A] to-[#20344A] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Oops!</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#172A3A] to-[#20344A] py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-2 mb-4">
            <Heart className="w-5 h-5 text-[#FFB800]" />
            <span className="text-white font-medium">Quick Check-In</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Hi {pulseData?.clientFirstName || 'there'}!
          </h1>
          <p className="text-white/80">
            {pulseData?.agentFirstName || 'Your agent'} wants to make sure you're feeling confident about your Medicare coverage.
          </p>
        </div>

        {/* Survey Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Q1: Plan Confidence */}
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Plan Confidence</h2>
                <p className="text-sm text-slate-600">Do you feel confident your current Medicare plan still fits your needs?</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'yes', label: 'Yes', emoji: '😊' },
                { value: 'not_sure', label: 'Not Sure', emoji: '🤔' },
                { value: 'no', label: 'No', emoji: '😟' }
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setQ1(opt.value)}
                  className={`p-4 rounded-xl border-2 transition text-center ${
                    q1 === opt.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="text-2xl block mb-1">{opt.emoji}</span>
                  <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Q2: Agent Trust */}
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Heart className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">Agent Support</h2>
                <p className="text-sm text-slate-600">Do you feel supported by your Medicare agent when you need help?</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'yes', label: 'Yes', emoji: '👍' },
                { value: 'sometimes', label: 'Sometimes', emoji: '👌' },
                { value: 'no', label: 'No', emoji: '👎' }
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setQ2(opt.value)}
                  className={`p-4 rounded-xl border-2 transition text-center ${
                    q2 === opt.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="text-2xl block mb-1">{opt.emoji}</span>
                  <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Q3: Would Call First */}
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">First Call</h2>
                <p className="text-sm text-slate-600">If a problem came up tomorrow, would you call me first?</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'yes', label: 'Yes, I would call you', emoji: '✅' },
                { value: 'no', label: 'No, probably not', emoji: '❌' }
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setQ3(opt.value)}
                  className={`p-4 rounded-xl border-2 transition text-center ${
                    q3 === opt.value
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="text-2xl block mb-1">{opt.emoji}</span>
                  <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Question (if any) */}
          {pulseData?.customQuestion && (
            <div className="p-6 border-b border-slate-100 bg-slate-50">
              <label className="block font-semibold text-slate-900 mb-2">
                {pulseData.customQuestion}
              </label>
              <input
                type="text"
                value={customAnswer}
                onChange={(e) => setCustomAnswer(e.target.value)}
                placeholder="Type your answer..."
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Open Response */}
          <div className="p-6 bg-slate-50">
            <label className="block font-semibold text-slate-900 mb-2">
              Anything else you want me to know? (Optional)
            </label>
            <textarea
              value={openResponse}
              onChange={(e) => setOpenResponse(e.target.value)}
              placeholder="Questions, concerns, or feedback..."
              rows={3}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="px-6 pb-4">
              <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-xl border border-red-200">
                {error}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="p-6 pt-0">
            <button
              type="submit"
              disabled={submitting || !q1 || !q2 || !q3}
              className="w-full py-4 bg-[#FFB800] text-[#172A3A] font-bold rounded-xl hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </button>
            <p className="text-center text-xs text-slate-500 mt-3">
              Takes less than 30 seconds
            </p>
          </div>
        </form>

        {/* Footer */}
        <p className="text-center text-white/60 text-xs mt-6">
          Powered by Pro IRP • Your information is secure
        </p>
      </div>
    </div>
  );
}
